const ROOT = document.documentElement;
const READY_CLASS = 'snack-gateway-ready';
const ACTIVE_CLASS = 'snack-gateway-active';

class SliceBrandGateway extends HTMLElement {
  connectedCallback() {
    this.handle = this.querySelector('[data-slice-handle]');
    this.status = this.querySelector('[data-slice-status]');
    this.designMode = this.dataset.designMode === 'true';
    this.gestureEnabled = this.dataset.gestureEnabled === 'true';
    this.drag = null;
    this.boundMove = this.onPointerMove.bind(this);
    this.boundEnd = this.onPointerEnd.bind(this);
    ROOT.classList.add(READY_CLASS);
    ROOT.classList.toggle('shopify-design-mode', this.designMode);
    if (!ROOT.dataset.snackBrand) ROOT.classList.add(ACTIVE_CLASS);
    this.querySelectorAll('[data-brand-select]').forEach((button) => button.addEventListener('click', () => this.selectBrand(button.dataset.brandSelect)));
    if (this.gestureEnabled && this.handle) {
      this.handle.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.handle.addEventListener('keydown', (event) => this.onKeyDown(event));
    }
    document.querySelectorAll('[data-brand-switch]').forEach((button) => button.addEventListener('click', () => this.resetGateway()));
    requestAnimationFrame(() => this.classList.add('is-ready'));
  }
  disconnectedCallback() { this.removeWindowListeners(); }
  get isMobile() { return window.matchMedia('(max-width: 749px)').matches; }
  onPointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return;
    this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, delta: 0 };
    this.handle.setPointerCapture?.(event.pointerId);
    this.classList.add('is-dragging');
    window.addEventListener('pointermove', this.boundMove, { passive: false });
    window.addEventListener('pointerup', this.boundEnd);
    window.addEventListener('pointercancel', this.boundEnd);
  }
  onPointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.id) return;
    event.preventDefault();
    const raw = this.isMobile ? event.clientY - this.drag.y : event.clientX - this.drag.x;
    const limit = (this.isMobile ? this.clientHeight : this.clientWidth) * .28;
    this.drag.delta = Math.max(-limit, Math.min(limit, raw));
    this.style.setProperty(this.isMobile ? '--slice-y' : '--slice-x', `${this.drag.delta}px`);
    const preview = this.brandFromDelta(this.drag.delta);
    this.handle.setAttribute('aria-valuenow', preview ? (preview === 'kk' ? '-1' : '1') : '0');
    this.handle.setAttribute('aria-valuetext', preview ? `${preview === 'kk' ? 'KK Cheese' : 'yamo+'} selected` : 'No brand selected');
  }
  onPointerEnd(event) {
    if (!this.drag || event.pointerId !== this.drag.id) return;
    const distance = this.drag.delta;
    this.drag = null;
    this.classList.remove('is-dragging');
    this.removeWindowListeners();
    const threshold = Math.min(100, (this.isMobile ? this.clientHeight : this.clientWidth) * .16);
    if (Math.abs(distance) >= threshold) this.selectBrand(this.brandFromDelta(distance));
    else this.resetHandle();
  }
  removeWindowListeners() {
    window.removeEventListener('pointermove', this.boundMove);
    window.removeEventListener('pointerup', this.boundEnd);
    window.removeEventListener('pointercancel', this.boundEnd);
  }
  brandFromDelta(delta) {
    if (Math.abs(delta) < 8) return null;
    if (this.isMobile) {
      const yamoFirst = this.classList.contains('slice-gateway--mobile-yamo-first');
      return delta < 0 ? (yamoFirst ? 'yamo' : 'kk') : (yamoFirst ? 'kk' : 'yamo');
    }
    return delta < 0 ? 'kk' : 'yamo';
  }
  onKeyDown(event) {
    const keyMap = { ArrowLeft: 'kk', ArrowRight: 'yamo', ArrowUp: 'kk', ArrowDown: 'yamo' };
    if (this.isMobile && this.classList.contains('slice-gateway--mobile-yamo-first')) { keyMap.ArrowUp = 'yamo'; keyMap.ArrowDown = 'kk'; }
    if (!keyMap[event.key]) return;
    event.preventDefault();
    this.selectBrand(keyMap[event.key]);
  }
  resetHandle() {
    this.style.setProperty('--slice-x', '0px');
    this.style.setProperty('--slice-y', '0px');
    this.handle?.setAttribute('aria-valuenow', '0');
    this.handle?.setAttribute('aria-valuetext', 'No brand selected');
  }
  selectBrand(brand) {
    if (!brand || this.classList.contains('is-entering')) return;
    this.dataset.selectedBrand = brand;
    this.classList.add('is-entering');
    if (this.status) this.status.textContent = `Opening ${brand === 'kk' ? 'KK Cheese' : 'yamo+'}`;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduced ? 20 : Number.parseInt(getComputedStyle(this).getPropertyValue('--slice-duration'), 10) || 900;
    window.setTimeout(() => {
      ROOT.dataset.snackBrand = brand;
      ROOT.classList.remove(ACTIVE_CLASS);
      this.classList.remove('is-entering');
      this.resetHandle();
      const destination = document.querySelector(`[data-brand-home="${brand}"]`);
      destination?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    }, duration);
  }
  resetGateway() {
    delete ROOT.dataset.snackBrand;
    ROOT.classList.add(ACTIVE_CLASS);
    this.dataset.selectedBrand = '';
    this.classList.remove('is-entering');
    this.resetHandle();
    window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    window.setTimeout(() => this.handle?.focus(), 100);
  }
}
if (!customElements.get('slice-brand-gateway')) customElements.define('slice-brand-gateway', SliceBrandGateway);
